/**
 * Spec 004 US3 (T017) — OCR-pipeline layer derivation.
 *
 * Pure function that maps the seven-state `processing_phase` enum
 * (plus optional audit-event signal for the LLM layer) into the
 * three user-visible OCR layers per research.md R4 + data-model.md
 * § "OcrLayerState".
 *
 * Per-layer state machine (research.md R4):
 *
 * | UI Layer     | running                       | success                                   | fail                                                   |
 * | ------------ | ----------------------------- | ----------------------------------------- | ------------------------------------------------------ |
 * | eRechnung    | phase === 'erechnung_check'   | phase advanced past `erechnung_check`     | phase==='failed' AND last-attempted=erechnung_check    |
 * |              |                               | AND source === 'erechnung'                |                                                        |
 * | Tesseract    | phase === 'ocr'               | phase advanced past `ocr`                 | phase==='failed' AND last-attempted=ocr                |
 * | LLM-Fallback | audit `ocr_llm_merge` started | audit `ocr_llm_merge` finished            | audit `ocr_llm_fail`                                   |
 *
 * `field_extract` and `summencheck` collapse into the layer that
 * produced their input — meaning eRechnung+Tesseract are "success"
 * by the time we reach those steps. `done` keeps both successful.
 *
 * **004 degradation note (per JJ HITL 2026-05-18, "Degrade")**: the
 * audit-event reader API rejects `entity_type='expense'` and the
 * `ocr_llm_*` actions on its zod schema, so 004 cannot fetch the
 * LLM audit pair without a backend delta (forbidden by R3=B). LLM
 * defaults to `idle` unless the caller injects `llmAuditEvent`
 * directly (kept as a future-friendly param so the unit-test fixture
 * can still exercise the four LLM states). Auto-injected follow-up:
 * `T-cdx-audit-projection` (open the LLM layer once the audit
 * reader is widened or a dedicated projection ships).
 */

export type OcrLayer = 'erechnung' | 'tesseract' | 'llm-fallback';
export type OcrLayerStatus = 'idle' | 'running' | 'success' | 'fail';

export interface OcrLayerDescriptor {
  layer: OcrLayer;
  status: OcrLayerStatus;
  /** Single-line German failure reason, only set when status='fail'. */
  failReason?: string;
}

export interface OcrLayersInput {
  /** Current value of `expense_processing_status.phase`. */
  processingPhase:
    | 'queued'
    | 'erechnung_check'
    | 'ocr'
    | 'field_extract'
    | 'summencheck'
    | 'done'
    | 'failed'
    | null
    | undefined;
  /** `expense_processing_status.error_message` when phase='failed'. */
  errorMessage?: string | null;
  /** `expenses.source` — disambiguates which layer ran first. */
  source?: string | null;
  /**
   * Optional injected LLM audit signal. In 004 the audit-reader API
   * can't surface `ocr_llm_*` actions for `entity_type='expense'`, so
   * production callers leave this undefined; the LLM layer then
   * stays `idle`. Unit tests inject this to exercise the success /
   * fail branches.
   */
  llmAuditEvent?: 'ocr_llm_merge' | 'ocr_llm_fail' | null;
}

const ERECHNUNG_HINT_REGEX = /erechnung|xml|zugferd|xrechnung|factur-?x/i;

function classifyFailedLayer(
  errorMessage: string | null | undefined,
  source: string | null | undefined,
): 'erechnung' | 'tesseract' {
  if (typeof errorMessage === 'string' && ERECHNUNG_HINT_REGEX.test(errorMessage)) {
    return 'erechnung';
  }
  if (source === 'erechnung') return 'erechnung';
  // Default: Tesseract OCR is the broadest fail surface (text PDFs,
  // image-only scans, malformed inputs).
  return 'tesseract';
}

function llmStateFromAudit(
  event: OcrLayersInput['llmAuditEvent'],
): OcrLayerStatus {
  if (event === 'ocr_llm_merge') return 'success';
  if (event === 'ocr_llm_fail') return 'fail';
  return 'idle';
}

export function deriveOcrLayers(input: OcrLayersInput): OcrLayerDescriptor[] {
  const phase = input.processingPhase ?? 'queued';
  let erechnung: OcrLayerStatus = 'idle';
  let tesseract: OcrLayerStatus = 'idle';
  let failReason: string | undefined;

  switch (phase) {
    case 'queued':
      // Both layers idle, LLM idle.
      break;
    case 'erechnung_check':
      erechnung = 'running';
      break;
    case 'ocr':
      // Past erechnung. If source='erechnung' the XML succeeded;
      // otherwise erechnung_check was skipped/non-applicable → idle.
      erechnung = input.source === 'erechnung' ? 'success' : 'idle';
      tesseract = 'running';
      break;
    case 'field_extract':
    case 'summencheck':
      // Post-OCR validation; the OCR layer that supplied the data is
      // a success at this point.
      erechnung = input.source === 'erechnung' ? 'success' : 'idle';
      tesseract = input.source === 'erechnung' ? 'idle' : 'success';
      break;
    case 'done':
      erechnung = input.source === 'erechnung' ? 'success' : 'idle';
      tesseract = input.source === 'erechnung' ? 'idle' : 'success';
      break;
    case 'failed': {
      const which = classifyFailedLayer(input.errorMessage, input.source);
      if (which === 'erechnung') {
        erechnung = 'fail';
      } else {
        // If we got far enough to fail in OCR, eRechnung either ran
        // and succeeded (rare) or was N/A. Don't pretend the XML
        // layer ran when we have no evidence.
        erechnung = input.source === 'erechnung' ? 'success' : 'idle';
        tesseract = 'fail';
      }
      failReason = input.errorMessage ?? 'OCR-Pipeline abgebrochen';
      break;
    }
  }

  const llm: OcrLayerStatus = llmStateFromAudit(input.llmAuditEvent);

  return [
    {
      layer: 'erechnung',
      status: erechnung,
      ...(erechnung === 'fail' && failReason ? { failReason } : {}),
    },
    {
      layer: 'tesseract',
      status: tesseract,
      ...(tesseract === 'fail' && failReason ? { failReason } : {}),
    },
    {
      layer: 'llm-fallback',
      status: llm,
      ...(llm === 'fail' ? { failReason: 'LLM-Fallback abgebrochen' } : {}),
    },
  ];
}

/** German display label per layer; one source of truth so component +
 *  tests stay aligned.
 *
 *  Codex Wave-C finding #2 (medium) — the second layer is NOT
 *  exclusively Tesseract. The backend's `probePdfTextLayer()` path
 *  (`apps/api/src/jobs/expense-process.ts:473-523`) extracts text
 *  directly from the PDF text layer without invoking Tesseract, yet
 *  the pipeline still transitions through `phase='ocr'` and
 *  `phase='field_extract'`. Renaming the layer to the engine-agnostic
 *  "Text-Extraktion" so the UI doesn't mis-attribute the extractor
 *  path until the backend exposes an explicit provenance signal. */
export const OCR_LAYER_LABEL: Record<OcrLayer, string> = {
  erechnung: 'eRechnung-XML',
  tesseract: 'Text-Extraktion',
  'llm-fallback': 'KI-Fallback',
};
